import React from "react";
import Card from "@vapor/react-material/Card";
import Box from "@vapor/react-material/Box";
import Section from "./Section";
import VaporHeaderBar from "../VaporHeaderBar";

export type vaporPageProps = {
  children?: React.ReactNode;
  variant?: "basic" | "landing";
  title?: string;
  subtitle?: string;
  headerLeft?: React.ReactNode[] | React.ReactNode;
  headerRight?: React.ReactNode[] | React.ReactNode;
  reducedHeader?: boolean;
  titleItems?: React.ReactNode[] | React.ReactNode;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: any;
  /**
   * If `true` card min-height of VaporPage is not full screen
   * @default false
   */
  disableFullHeight?: boolean;
  /**
   * Content prop that allows to add VaporToolbar component that stand fixed on bottom VaporPage
   */
  contentToolbar?: React.ReactNode;
};

export const VaporPage = (props: vaporPageProps) => {
  const {
    children,
    variant = "basic",
    title,
    subtitle,
    headerRight,
    headerLeft,
    reducedHeader = false,
    titleItems,
    sx,
    disableFullHeight,
    contentToolbar
  } = props;
  const Wrapper = variant === "landing" ? Box : Card;

  const isLandingVariant = variant === "landing";

  const refToolbarHeight = React.useRef(null);
  const [toolbarHeight, setToolbarHeight] = React.useState(0);
  React.useLayoutEffect(() => {
    setToolbarHeight(refToolbarHeight?.current?.offsetHeight);
  }, [contentToolbar]);

  const pageContentStyle = {
    paddingLeft: "24px",
    paddingRight: "24px",
    "@media screen and (min-width: 1536px)": {
      paddingLeft: "32px",
      paddingRight: "32px"
    },
    "@media screen and (max-width: 1200px)": {
      paddingLeft: "16px",
      paddingRight: "16px"
    }
  };
  const PageContent = Box;

  const wrapperStyle =
    variant === "landing"
      ? {
          maxWidth: "1184px",
          margin: "auto",
          marginTop: "40px",
          marginBottom: "40px",
          "@media screen and (max-width: 1232px)": {
            marginLeft: "24px",
            marginRight: "24px"
          }
        }
      : {
          margin: "auto",
          padding: 0,
          minHeight:
            !disableFullHeight && `calc(100vh - 80px - ${toolbarHeight}px)`,
          "@media screen and (max-width: 1536px)": {
            minHeight:
              !disableFullHeight && `calc(100vh - 48px - ${toolbarHeight}px)`
          }
        };

  const wrapperStylePage = {
    margin: 0,
    backgroundColor: "primary.background",
    minHeight: `calc(100vh - 48px - ${toolbarHeight}px)`,
    padding: !isLandingVariant ? "16px" : 0,
    paddingTop: isLandingVariant ? "40px" : "16px",
    paddingBottom: isLandingVariant ? "40px" : "16px",
    textAlign: "left",
    maxWidth: "100%",
    "@media screen and (max-width: 1536px)": {
      minHeight: `calc(100vh - 48px - ${toolbarHeight}px)`,
      padding: !isLandingVariant ? "0px !important" : 0,
      paddingTop: isLandingVariant ? "40px" : 0,
      paddingBottom: isLandingVariant ? "40px" : 0
    }
  };
  return (
    <Box>
      <Box
        sx={{
          ...wrapperStylePage
        }}
      >
        <Wrapper
          sx={{
            ...wrapperStyle,
            ...sx
          }}
        >
          {(title || subtitle || headerRight || headerLeft || titleItems) && (
            <VaporHeaderBar
              title={title}
              description={subtitle}
              leftItems={headerLeft}
              rightItems={headerRight}
              titleItems={titleItems}
              reduced={reducedHeader}
            />
          )}
          <PageContent>{children}</PageContent>
        </Wrapper>
      </Box>
      <div ref={refToolbarHeight}>{contentToolbar}</div>
    </Box>
  );
};

VaporPage.Section = Section;
